/**
 * Comprovante enviado pelo cliente (spec 039 e 047): a mensalidade e a mensalidade antecipada do avanco sao pagas pelo
 * PIX da marca, e o cliente manda o comprovante pela area na mesma tela. Tres lugares (3.7): a fila em Financeiro >
 * Comprovantes (os em analise primeiro), a ficha da pessoa e, na mensalidade e no pedido, o comprovante que chegou pra
 * eles. No topo da fila, o PIX de recebimento: a chave que a area mostra pro cliente pagar (spec 047, 2.5).
 *
 * Conferir abre o arquivo ao lado do que ele paga, pra comparar na mesma tela. Conferir da a baixa numa acao so (3.4) e
 * libera a area; recusar pede o motivo, que o cliente le no sino, no e-mail e no Perfil. O que vale e o backend quem diz.
 */
import {
  Abas, Aviso, Botao, CabecalhoDePagina, CartaoDeComprovante, Carregando, ConferenciaDeComprovante, Dialogo, Fatos, LinkInterno, ListaDeComprovantes, Pilha, Secao, Selo, Tabela, TOM_DO_COMPROVANTE, Vazio,
  Campo, CampoSelecao, CampoTextoLongo, ROTULO_DA_CHAVE_PIX, VisorDeArquivo, formatarChavePix, formatarDataHora, formatarDia, formatarReais, type Comprovante, type EstadoDoComprovante, type TipoDeChavePix,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { erroDe, useAcao, useApi, usePapel } from "../api";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Filtro = EstadoDoComprovante | "TODOS";
const FILTROS: Filtro[] = ["EM_ANALISE", "CONFERIDO", "RECUSADO", "TODOS"];

function useFila(estado: Filtro, ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["comprovantes", estado], queryFn: () => ler<{ itens: Comprovante[] }>(`/v1/admin/comprovantes?estado=${estado}`), enabled: ligado });
}

export function useComprovantesDaPessoa(pessoaId: string | undefined) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["comprovantes", "pessoa", pessoaId], queryFn: () => ler<{ itens: Comprovante[] }>(`/v1/admin/pessoas/${pessoaId}/comprovantes`), enabled: Boolean(pessoaId) });
}

/** O arquivo do comprovante, baixado pela API autenticada (cada abertura fica na trilha) e mostrado na tela. */
function useArquivo(c: Comprovante) {
  const { arquivo } = useApi();
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState<string>();
  useEffect(() => {
    let vivo = true;
    let criada: string | null = null;
    setUrl(null);
    setErro(undefined);
    arquivo(`/v1/admin/comprovantes/${c.id}/arquivo`).then(
      (blob) => { criada = URL.createObjectURL(blob); if (vivo) setUrl(criada); else URL.revokeObjectURL(criada); },
      (e) => { if (vivo) setErro(erroDe(e)); },
    );
    return () => { vivo = false; if (criada) URL.revokeObjectURL(criada); };
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return { url, erro, carregando: !url && !erro };
}

const oQueAcontece = (c: Comprovante) =>
  c.alvo.tipo === "MENSALIDADE"
    ? "A mensalidade fica paga, o bloqueio dela sai da área do cliente e ele é avisado. Não precisa marcar paga em outra tela."
    : "O pagamento do avanço fica confirmado e o pedido entra na aba Avanços de Solicitações, esperando a conta na extranet.";

/**
 * O arquivo ao lado dos dados, com Conferir e Recusar quando ainda esta em analise. `aoDecidir` recebe a frase do que
 * aconteceu, pra tela mostrar em cima.
 */
export function ConferirComprovante({ c, aoDecidir }: { c: Comprovante; aoDecidir: (texto: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const arq = useArquivo(c);
  const acao = useAcao();
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  const [motivo, setMotivo] = useState("");
  const pode_decidir = pode("SUPORTE");
  const darBaixa = async () => {
    let r: { ok: boolean; deu_baixa: boolean } | undefined;
    const ok = await acao.rodar(async () => { r = await postar<{ ok: boolean; deu_baixa: boolean }>(`/v1/admin/comprovantes/${c.id}/conferir`); });
    if (!ok) return;
    setConferir(false);
    await qc.invalidateQueries();
    aoDecidir(
      !r?.deu_baixa ? "Comprovante conferido. Não havia o que baixar: o pagamento já estava registrado."
      : c.alvo.tipo === "MENSALIDADE" ? "Comprovante conferido e mensalidade paga. O cliente foi avisado e a área dele desbloqueia na hora."
      : "Comprovante conferido e pagamento do avanço confirmado. O pedido entrou na aba Avanços de Solicitações.",
    );
  };
  const links = (
    <>
      {c.pessoa && <LinkInterno href={`/traders/${c.pessoa.id}`}>Ficha do cliente</LinkInterno>}
      <LinkInterno href={`/matriculas/${c.matricula_id}`}>Ver a inscrição</LinkInterno>
      {c.alvo.tipo === "PEDIDO" && <LinkInterno href={`/solicitacoes/${c.alvo.id}`}>Ver o pedido</LinkInterno>}
    </>
  );
  return (
    <>
      <ConferenciaDeComprovante
        c={c}
        visor={<VisorDeArquivo url={arq.url} tipo={c.arquivo.tipo} nome={c.arquivo.nome} carregando={arq.carregando} erro={arq.erro} />}
        acoes={c.estado === "EM_ANALISE" ? (
          <>
            <Botao disabled={!pode_decidir} onClick={() => { acao.limpar(); setConferir(true); }}>Conferir e dar a baixa</Botao>
            <Botao variante="secundario" disabled={!pode_decidir} onClick={() => setRecusar(true)}>Recusar</Botao>
          </>
        ) : undefined}
        links={links}
      />
      <Dialogo
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        titulo="Conferir e dar a baixa"
        descricao={oQueAcontece(c)}
        rodape={<><Botao variante="fantasma" onClick={() => setConferir(false)}>Voltar</Botao><Botao carregando={acao.carregando} onClick={() => void darBaixa()}>Conferir e dar a baixa</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              ...(c.pessoa ? [{ rotulo: "Cliente", valor: c.pessoa.nome, detalhe: c.pessoa.cpf }] : []),
              { rotulo: "O que paga", valor: c.descricao },
              { rotulo: "Valor", valor: c.valor_centavos != null ? formatarReais(c.valor_centavos) : "Sem valor", mono: true },
              { rotulo: "Pago em", valor: formatarDia(c.pago_no_dia), detalhe: "segundo o cliente" },
            ]}
          />
        </div>
      </Dialogo>
      <DialogoDeAcao
        aberto={recusar}
        aoFechar={() => setRecusar(false)}
        titulo="Recusar o comprovante"
        descricao={`${c.pessoa ? `${c.pessoa.nome}, ` : ""}${c.descricao}. O motivo vai pro cliente no aviso, no e-mail e no Perfil; ele pode pagar de novo pelo PIX ou só mandar outro comprovante. Até lá a área dele segue bloqueada.`}
        botao="Recusar comprovante"
        variante="perigo"
        podeEnviar={pode_decidir && motivo.trim().length >= 5}
        aoAbrir={() => setMotivo("")}
        aoEnviar={() => postar(`/v1/admin/comprovantes/${c.id}/recusar`, { motivo: motivo.trim() })}
        aoConcluir={() => { void qc.invalidateQueries(); aoDecidir("Comprovante recusado. O cliente foi avisado com o motivo e pode mandar outro."); }}
      >
        <CampoTextoLongo rotulo="Motivo (o cliente lê)" valor={motivo} aoMudar={setMotivo} maxLength={500} required placeholder="Ex.: o valor do comprovante não confere com a mensalidade." dica="Pelo menos 5 letras. Diga o que ele precisa fazer." />
      </DialogoDeAcao>
    </>
  );
}

// ---------------------------------------------------------------- fila (Financeiro > Comprovantes)

export function PaginaComprovantes() {
  const [params, setParams] = useSearchParams();
  const estado = (FILTROS.includes(params.get("estado") as Filtro) ? params.get("estado") : "EM_ANALISE") as Filtro;
  const escolhido = params.get("comprovante");
  const lista = useFila(estado);
  const emAnalise = useFila("EM_ANALISE");
  // Link do sino ou da ficha pra um comprovante que nao esta na aba aberta (ja decidido): procura em todos.
  const naLista = lista.data?.itens.find((c) => c.id === escolhido);
  const todos = useFila("TODOS", Boolean(escolhido) && lista.isSuccess && !naLista);
  const c = naLista ?? todos.data?.itens.find((x) => x.id === escolhido);
  const [feito, setFeito] = useState<string>();
  const topo = useRef<HTMLDivElement>(null);
  const mudar = (novo: { estado?: Filtro; comprovante?: string | null }) => {
    const p = new URLSearchParams(params);
    const e = novo.estado ?? estado;
    if (e === "EM_ANALISE") p.delete("estado"); else p.set("estado", e);
    if (novo.comprovante !== undefined) { if (novo.comprovante) p.set("comprovante", novo.comprovante); else p.delete("comprovante"); }
    setParams(p, { replace: novo.comprovante === undefined });
  };
  useEffect(() => { if (escolhido && c) topo.current?.scrollIntoView({ block: "start", behavior: "smooth" }); }, [escolhido, Boolean(c)]); // eslint-disable-line react-hooks/exhaustive-deps
  const vazio = estado === "EM_ANALISE" ? "Nenhum comprovante esperando. Quando um cliente enviar, ele aparece aqui e no sino." : estado === "CONFERIDO" ? "Nenhum comprovante conferido ainda." : estado === "RECUSADO" ? "Nenhum comprovante recusado." : "Nenhum cliente enviou comprovante ainda.";
  return (
    <Pilha espaco={5}>
      <div ref={topo} style={{ scrollMarginTop: "var(--e-5)" }}>
        <CabecalhoDePagina titulo="Comprovantes" descricao="O cliente paga a mensalidade e o avanço pelo PIX abaixo e manda o comprovante pela área. Conferir dá a baixa e tira o bloqueio; recusar pede o motivo, que o cliente lê. Ele leu que a liberação leva até 1 dia." />
      </div>
      <PixDeRecebimento />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {escolhido && (
        c ? (
          <Secao destaque rotulo={c.pessoa?.nome ?? "Comprovante"} titulo={c.estado === "EM_ANALISE" ? "Conferir o comprovante" : `Comprovante ${c.estado_rotulo.toLowerCase()}`} acoes={<><Selo tom={TOM_DO_COMPROVANTE[c.estado]}>{c.estado_rotulo}</Selo><Botao variante="fantasma" tamanho="pequeno" onClick={() => mudar({ comprovante: null })}>Fechar</Botao></>}>
            <ConferirComprovante key={c.id} c={c} aoDecidir={(t) => { setFeito(t); mudar({ comprovante: null }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          </Secao>
        ) : lista.isLoading || todos.isLoading ? <Carregando compacto texto="Abrindo o comprovante" />
        : <Aviso tipo="alerta">Este comprovante não foi encontrado. Pode ter sido apagado com o cadastro de teste.</Aviso>
      )}
      <Abas
        rotulo="Comprovantes"
        valor={estado}
        aoMudar={(v) => { setFeito(undefined); mudar({ estado: v }); }}
        abas={[
          { valor: "EM_ANALISE", rotulo: "Em análise", contagem: emAnalise.data?.itens.length },
          { valor: "CONFERIDO", rotulo: "Conferidos" },
          { valor: "RECUSADO", rotulo: "Recusados" },
          { valor: "TODOS", rotulo: "Todos" },
        ]}
      />
      {lista.isLoading ? <Carregando compacto texto="Buscando os comprovantes" /> : lista.isError ? <Aviso tipo="erro">{erroDe(lista.error)}</Aviso> : (
        <Tabela
          linhas={lista.data?.itens ?? []}
          chave={(x) => x.id}
          destaque={(x) => x.id === escolhido}
          aoClicar={(x) => { setFeito(undefined); mudar({ comprovante: x.id }); }}
          vazio={<Vazio titulo="Nada por aqui">{vazio}</Vazio>}
          colunas={[
            { chave: "cliente", rotulo: "Cliente", celula: (x) => x.pessoa ? <><LinkInterno href={`/traders/${x.pessoa.id}`}>{x.pessoa.nome}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{x.pessoa.cpf}</span></> : "Sem cadastro" },
            { chave: "paga", rotulo: "O que paga", celula: (x) => <>{x.descricao}{x.observacao && <span className="mtc-tabela__apoio">Obs.: {x.observacao}</span>}</> },
            { chave: "valor", rotulo: "Valor", celula: (x) => <span className="mtc-mono">{x.valor_centavos != null ? formatarReais(x.valor_centavos) : "Sem valor"}</span> },
            { chave: "pago", rotulo: "Pago em", celula: (x) => <><span className="mtc-mono">{formatarDia(x.pago_no_dia)}</span><span className="mtc-tabela__apoio">enviado {formatarDataHora(x.enviado_em)}</span></> },
            { chave: "estado", rotulo: "Situação", celula: (x) => <><Selo tom={TOM_DO_COMPROVANTE[x.estado]}>{x.estado_rotulo}</Selo>{x.estado !== "EM_ANALISE" && x.decidido_por && <span className="mtc-tabela__apoio">por {x.decidido_por}{x.motivo ? `: ${x.motivo}` : ""}</span>}</> },
            { chave: "acao", rotulo: "Abrir", rotuloOculto: true, celula: (x) => <Botao variante={x.estado === "EM_ANALISE" ? "primario" : "secundario"} tamanho="pequeno" onClick={() => { setFeito(undefined); mudar({ comprovante: x.id }); }}>{x.estado === "EM_ANALISE" ? "Conferir" : "Ver"}</Botao> },
          ]}
        />
      )}
    </Pilha>
  );
}

// ---------------------------------------------------------------- PIX de recebimento (spec 047, 2.5)

type PixCadastrado = { tipo: TipoDeChavePix; chave: string; chave_formatada: string; favorecido: string; cidade: string; atualizado_em: string; atualizado_por: string };
const TIPOS_DE_CHAVE: TipoDeChavePix[] = ["CNPJ", "CPF", "EMAIL", "TELEFONE", "ALEATORIA"];
/** Como cada tipo se digita (o mesmo da chave do repasse no Perfil da area): o exemplo e o teclado. A validacao e do backend. */
const COMO_DIGITAR: Record<TipoDeChavePix, { exemplo: string; teclado: "numeric" | "email" | "tel" | "text" }> = {
  CPF: { exemplo: "000.000.000-00", teclado: "numeric" },
  CNPJ: { exemplo: "00.000.000/0000-00", teclado: "numeric" },
  EMAIL: { exemplo: "financeiro@exemplo.com", teclado: "email" },
  TELEFONE: { exemplo: "(11) 98765-4321", teclado: "tel" },
  ALEATORIA: { exemplo: "0000aaaa-00aa-00aa-00aa-000000aaaaaa", teclado: "text" },
};
/** O nome e a cidade vao inteiros (a razao social nao cabia em 25, Vitor 27/10); so o QR corta, em 25 e 15. */
const FAVORECIDO = 100;
const CIDADE = 60;

/**
 * A chave PIX que a area mostra pro cliente que escolhe o PIX direto pra pagar a mensalidade e o avanco (spec 047, 2.5
 * e 2.10). O dinheiro cai direto nessa conta; a baixa e do administrador, pelo comprovante. Sem chave, a area so oferece
 * a Pagar.me (PIX ou cartao).
 * Mudar vale na hora pra todo PIX novo; a trilha guarda antes e depois.
 */
function PixDeRecebimento() {
  const { ler, por } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["pix-de-recebimento"], queryFn: () => ler<{ pix: PixCadastrado | null }>("/v1/admin/pix") });
  const [aberto, setAberto] = useState(false);
  const [feito, setFeito] = useState<string>();
  const [f, setF] = useState({ tipo: "" as TipoDeChavePix | "", chave: "", favorecido: "", cidade: "" });
  const pix = q.data?.pix ?? null;
  const abrir = () => {
    setFeito(undefined);
    // O telefone vem com +55 (como o PIX guarda); o campo e o mesmo do Perfil, com DDD.
    setF(pix ? { tipo: pix.tipo, chave: formatarChavePix(pix.tipo, pix.tipo === "TELEFONE" ? pix.chave.replace(/^\+55/, "") : pix.chave), favorecido: pix.favorecido, cidade: pix.cidade } : { tipo: "", chave: "", favorecido: "", cidade: "" });
    setAberto(true);
  };
  const pronto = Boolean(f.tipo) && f.chave.trim().length > 0 && f.favorecido.trim().length >= 2 && f.cidade.trim().length >= 2;
  const como = f.tipo ? COMO_DIGITAR[f.tipo] : null;
  const podeMudar = pode("ADMIN");
  return (
    <Secao
      titulo="PIX de recebimento"
      acoes={q.isSuccess && podeMudar ? <Botao variante={pix ? "secundario" : "primario"} tamanho="pequeno" onClick={abrir}>{pix ? "Mudar" : "Cadastrar"}</Botao> : undefined}
    >
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>A chave que a área mostra pra quem escolhe pagar a mensalidade ou o avanço pelo PIX direto, em vez de PIX ou cartão pela Pagar.me. O dinheiro cai direto nesta conta; a baixa é sua, pelo comprovante.</p>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando a chave PIX" />
        : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso>
        : !pix ? <Aviso tipo="alerta"><strong>Nenhuma chave cadastrada.</strong> Sem ela, a área só oferece PIX ou cartão pela Pagar.me; o PIX direto aparece quando a chave estiver aqui.</Aviso>
        : (
          <Fatos
            itens={[
              { rotulo: "Chave", valor: pix.chave_formatada, detalhe: ROTULO_DA_CHAVE_PIX[pix.tipo] ?? pix.tipo, mono: true },
              { rotulo: "Favorecido", valor: pix.favorecido },
              { rotulo: "Cidade", valor: pix.cidade },
              { rotulo: "Última mudança", valor: formatarDataHora(pix.atualizado_em), detalhe: `por ${pix.atualizado_por}` },
            ]}
          />
        )}
      <DialogoDeAcao
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo={pix ? "Mudar o PIX de recebimento" : "Cadastrar o PIX de recebimento"}
        descricao="Vale na hora pra todo PIX novo que a área mostrar. O nome e a cidade vão no QR Code sem acento: use os da conta, como o banco mostra."
        botao="Salvar chave"
        podeEnviar={pronto}
        aoEnviar={() => por("/v1/admin/pix", { tipo: f.tipo, chave: f.chave.trim(), favorecido: f.favorecido.trim(), cidade: f.cidade.trim() })}
        aoConcluir={() => { void qc.invalidateQueries({ queryKey: ["pix-de-recebimento"] }); setFeito(pix ? "Chave PIX mudada. O próximo PIX que o cliente abrir já sai com ela." : "Chave PIX cadastrada. O cliente já consegue pagar pela área."); }}
      >
        <CampoSelecao rotulo="Tipo da chave" valor={f.tipo} aoMudar={(tipo) => setF({ ...f, tipo, chave: f.chave ? formatarChavePix(tipo, f.chave) : "" })} vazio="Escolha o tipo" required opcoes={TIPOS_DE_CHAVE.map((t) => ({ valor: t, rotulo: ROTULO_DA_CHAVE_PIX[t] }))} />
        <Campo rotulo="Chave" valor={f.chave} aoMudar={(v) => setF({ ...f, chave: f.tipo ? formatarChavePix(f.tipo, v) : v })} mono={f.tipo !== "EMAIL"} inputMode={como?.teclado} autoCapitalize="none" autoComplete="off" spellCheck={false} maxLength={77} placeholder={como?.exemplo} dica={f.tipo ? "Do jeito que o banco mostra. O sistema confere antes de salvar." : "Escolha o tipo antes: a chave ganha o formato certo."} />
        <Campo rotulo="Nome do favorecido" valor={f.favorecido} aoMudar={(favorecido) => setF({ ...f, favorecido })} maxLength={FAVORECIDO} contador placeholder="Ex.: Mesa de Teste Ltda" dica="A razão social, como está no banco. No QR Code vão só as 25 primeiras letras, sem acento." />
        <Campo rotulo="Cidade" valor={f.cidade} aoMudar={(cidade) => setF({ ...f, cidade })} maxLength={CIDADE} contador placeholder="Ex.: São Paulo" dica="A cidade da conta. No QR Code vão só as 15 primeiras letras." />
      </DialogoDeAcao>
    </Secao>
  );
}

// ---------------------------------------------------------------- ficha da pessoa

/** Ficha da pessoa (3.7 a): todos os comprovantes dela; o em analise abre ali mesmo, com o arquivo e as decisoes. */
export function ComprovantesDaPessoa({ pessoaId, aoDecidir }: { pessoaId: string; aoDecidir: (texto: string) => void }) {
  const q = useComprovantesDaPessoa(pessoaId);
  const [aberto, setAberto] = useState<string | null>(null);
  const itens = q.data?.itens ?? [];
  const emAnalise = itens.filter((c) => c.estado === "EM_ANALISE").length;
  return (
    <Secao titulo="Comprovantes" acoes={emAnalise ? <Selo tom="info">{emAnalise === 1 ? "1 em análise" : `${emAnalise} em análise`}</Selo> : undefined}>
      {q.isLoading ? <Carregando compacto texto="Buscando os comprovantes" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : !itens.length ? (
        <Vazio titulo="Nenhum comprovante">Quando a pessoa pagar a mensalidade ou o avanço pelo PIX e enviar o comprovante pela área, ele aparece aqui.</Vazio>
      ) : (
        <ListaDeComprovantes rotulo="Comprovantes da pessoa">
          {itens.map((c) => (
            <div role="listitem" key={c.id}>
              <CartaoDeComprovante
                c={c}
                destaque={aberto === c.id}
                acoes={<Botao variante={c.estado === "EM_ANALISE" && aberto !== c.id ? "primario" : "secundario"} tamanho="pequeno" aria-expanded={aberto === c.id} onClick={() => setAberto(aberto === c.id ? null : c.id)}>{aberto === c.id ? "Fechar" : c.estado === "EM_ANALISE" ? "Abrir e conferir" : "Ver o arquivo"}</Botao>}
              >
                {aberto === c.id && <ConferirComprovante c={c} aoDecidir={(t) => { setAberto(null); aoDecidir(t); }} />}
              </CartaoDeComprovante>
            </div>
          ))}
        </ListaDeComprovantes>
      )}
    </Secao>
  );
}

// ---------------------------------------------------------------- na mensalidade e no pedido

/**
 * O comprovante que chegou pra uma mensalidade ou um pedido (3.7 b): o ultimo enviado, com o estado e o link pra
 * conferencia. `aviso`: a frase inteira, pra onde a equipe decide (o pedido esperando pagamento).
 */
export function ComprovanteDoAlvo({ pessoaId, tipo, id, aviso }: { pessoaId: string | undefined; tipo: Comprovante["alvo"]["tipo"]; id: string; aviso?: boolean }) {
  const q = useComprovantesDaPessoa(pessoaId);
  const c = q.data?.itens.find((x) => x.alvo.tipo === tipo && x.alvo.id === id);
  if (!c) return null;
  const link = <LinkInterno href={`/comprovantes?comprovante=${c.id}`}>{c.estado === "EM_ANALISE" ? "Conferir o comprovante" : "Ver o comprovante"}</LinkInterno>;
  if (aviso) {
    return c.estado === "EM_ANALISE"
      ? <Aviso tipo="info">O cliente enviou um comprovante em {formatarDataHora(c.enviado_em)}, pago em {formatarDia(c.pago_no_dia)} segundo ele. {link}: conferir já confirma o pagamento.</Aviso>
      : <Aviso tipo={c.estado === "CONFERIDO" ? "sucesso" : "alerta"}>Comprovante enviado pelo cliente: {c.estado_rotulo.toLowerCase()}{c.motivo ? ` (${c.motivo})` : ""}. {link}</Aviso>;
  }
  return (
    <span className="mtc-linha" style={{ gap: "var(--e-2)", fontSize: "var(--t-sm)", flexBasis: "100%" }}>
      <Selo tom={TOM_DO_COMPROVANTE[c.estado]}>Comprovante {c.estado_rotulo.toLowerCase()}</Selo>
      {link}
    </span>
  );
}
